class NavigationComponent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Options
    this.options = {
      classToToggle: 'menu-is-open',
      closeSiblings: true,
      closeOnMouseleave: false,
      isDesktopMenu: false,
      trapFocus: true,
    };

    // Get options from element data and combine with this.options
    if (this?.dataset?.options) {
      const dataOptions = JSON.parse(this.dataset.options);
      this.options = {
        ...this.options,
        ...dataOptions,
      };
    }

    this.options.isDesktopMenu = (window.windowWidth >= 1024) ? true : false;
    this.triggers = this.querySelectorAll('[data-menu-trigger]');
    this.returnTriggers = this.querySelectorAll('[data-menu-return-trigger]');

    // Used for the main menu to retain the nested levels for the mobile menu
    this.state = [this.querySelector('nav')];
    this.timeout = null;

    this.listeners = {
      debounceClickEvent: this.debounceClickEvent.bind(this),
      debouncedOnMouse: this.debouncedOnMouse.bind(this),
      debouncedReturnClickEvent: this.debounceReturnClickEvent.bind(this),
      resizeListener: () => {
        this.options.isDesktopMenu = (window.windowWidth >= 1024) ? true : false;

        this.triggers.forEach(trigger => {
          if (trigger.classList.contains('level-1') && this.options.isDesktopMenu) {
            trigger.addEventListener('mouseenter', this.listeners.debounceClickEvent);
          }
          else {
            trigger.removeEventListener('mouseenter', this.listeners.debounceClickEvent);
          }
        });
      },
    };

    // Init elements
    this.groups = this.hasAttribute('data-menu-group')
      ? [this.closest('[data-menu-group]')]
      : this.querySelectorAll('[data-menu-group]');

    window.addEventListener('window:resize', this.listeners.resizeListener);

    this.triggers.forEach(trigger => {
      trigger.addEventListener('click', this.listeners.debounceClickEvent);
      if (trigger.classList.contains('level-1') && this.options.isDesktopMenu) {
        trigger.addEventListener('mouseenter', this.listeners.debounceClickEvent);
      }
    });
    this.returnTriggers.forEach(trigger => {
      trigger.addEventListener('click', this.listeners.debouncedReturnClickEvent);
    });

    if (this.options.closeOnMouseleave) {
      this.addEventListener('mouseleave', this.listeners.debouncedOnMouse('closeAll'));
    }

    window.addEventListener('window:search-performed', () => {
      document.body.classList.remove('main-menu-is-open');
    });
  }

  disconnectedCallback() {
    window.removeEventListener('window:resize', this.listeners.resizeListener);
    window.removeEventListener('window:search-performed', () => {
      document.body.classList.remove('main-menu-is-open');
    });
  }

  debounceClickEvent(event) {
    if (!event) return false;

    const trigger = event.currentTarget;
    const buttonType = trigger.nodeName.toLowerCase();
    const group = trigger.closest('[data-menu-group]');

    if (!group) return false;

    const isOpen = group.classList.contains(this.options.classToToggle);

    if (event.type === 'click' && isOpen) {
      if (trigger.dataset?.url && trigger.dataset.url !== '#') {
        window.location.href = trigger.dataset.url;
      }
      return;
    }

    if (!isOpen || buttonType !== 'a') {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.open(group);

      if (this.options.isDesktopMenu) {
        group.addEventListener('mouseleave', () => {
          this.close(group);
        });
      }
    }
  }

  debouncedOnMouse() {
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      this.closeAll();
    }, 0);
  }

  debounceReturnClickEvent(event) {
    if (!event) return false;

    const group = event.target.closest('[data-menu-group]');
    this.close(group);
  }

  openFirstGroup() {
    this.groups.forEach((group, index) => {
      if (index === 0) {
        this.open(group, true);
      }
    });
  }

  open(group, init = false) {
    if (!group) return false;

    this.closeSiblings(group);

    group.classList.add(this.options.classToToggle);

    // Init is only used to open level-2 on desktop, but this can happen when the menu is not opened. Also only focus the entire menu.
    (!init) && trapFocus(group.closest('#mainMenu'));

    group.closest('#mainMenu')?.querySelectorAll('.main-menu__link')?.[0]?.focus();

    // to prevent different heights for each panel, we have to disable to overflow for hidden panels
    const parentSubmenu = group.closest('.main-menu__submenu') || group.closest('.main-menu__content') ;
    if (parentSubmenu) {
      parentSubmenu.scrollTop = 0;
      parentSubmenu.classList.remove('overflow-y-auto');

      if (window.windowWidth < 1024) {
        parentSubmenu.classList.add('overflow-hidden');
      }
    }

    // Keep track of the state so we know where to add the focus when moving between the mobile menu items
    this.state.push(group);

  }

  close(group, focus = true) {
    if (!group) return false;

    if (focus) {
      removeTrapFocus(group);

      // Set the focus to the trigger when closing a collapsible
      group.querySelector('[data-menu-trigger]').focus();
    }

    // Close active group
    group.classList.remove(this.options.classToToggle);
  }

  closeSiblings(currentGroup = null) {
    this.groups.forEach(group => {
      if (group !== currentGroup && !group.contains(currentGroup) && !currentGroup.contains(group)) this.close(group, false);
    });
  }

  openAll() {
    this.groups.forEach(group => group.classList.add(this.options.classToToggle));
  }

  closeAll() {
    this.groups.forEach(group => group.classList.remove(this.options.classToToggle));

    this.state = [this.querySelector('nav')];
  }
}
if (!customElements.get('navigation-component')) {
  customElements.define('navigation-component', NavigationComponent);
}